<script lang="ts" setup>
import type { AsyncDataRequestStatus } from '#app'
import CardWithImage from '~/components/cards/CardWithImage.vue'
import BasicButton from '~/components/form-elements/BasicButton.vue'
import SearchInput from '~/components/form-elements/SearchInput.vue'
import { useWindowHelpers } from '~/composables/window-helpers'
import PageEnum from '~/enums/page'
import helpers from '~/helpers'
import JsonApiHelper from '~/helpers/json-api-helper'
import type { BreadcrumbsItem } from '~/types/breadcrumbs'
import type { Page } from '~/types/json-api'

const { $apiHelper, $toast } = useNuxtApp()
const route = useRoute()
const router = useRouter()
const { scrollToSavedPosition } = useWindowHelpers()

const breadcrumbsItems: BreadcrumbsItem[] = [{ name: 'Главная', path: PageEnum.Main }]
const messages = { failedToGetArticles: 'Не удалось получить статьи.' }

// Data.
const articlesData = ref<any[]>([])
const debounce = ref<any>(null)
const isDataLoading = ref(true)

// Filter.
const filter = ref({ isBlog: null, query: null })

// Pagination.
const page = ref<Page>({ number: 1, size: 36 })
const totalArticles = ref<number | null>(null)
const totalPages = ref<number | null>(null)

const { status } = await useLazyAsyncData(loadData)

useHead({
  title: 'Полезные статьи о БАДах и витаминах — iTAB',
  meta: [{
    name: 'description',
    content: 'В серии статей вы узнаете больше о поддержании здоровья и влиянии биологически активных веществ растительного, животного или минерального происхождения.'
  }]
})

const showSearchInput = computed(() => filter.value.query || filter.value.query === '' || articlesData.value.length)
const title = computed(() => filter.value.isBlog ? 'Блог' : 'Статьи')

watch(
  () => filter.value.query,
  (): void => {
    if (debounce.value) {
      clearTimeout(debounce.value)
    }

    debounce.value = setTimeout(
      async () => {
        page.value.number = 1

        await loadData()
      },
      500
    )
  }
)
watch(status, (x: AsyncDataRequestStatus): void => {
  if (x === 'success') {
    scrollToSavedPosition()
  }
})
watch(
  () => route.query,
  async (): Promise<void> => {
    window.scrollTo(0, 0)

    parseQuery()

    await loadData()
  }
)

async function changePage (pageNumber: number): Promise<void> {
  window.scrollTo(0, 0)

  page.value.number = pageNumber

  await loadData()
}

async function goToArticlesPage (): Promise<void> {
  await router.push(PageEnum.Articles)
}

async function loadData (): Promise<void> {
  isDataLoading.value = true

  articlesData.value = []
  totalPages.value = null

  const { isBlog, query } = filter.value
  const { number, size } = page.value
  const params = helpers.getParams([
    { name: 'filter[isBlog]', value: isBlog, condition: isBlog },
    { name: 'filter[query]', value: query, condition: query },
    { name: 'page[number]', value: number, condition: true },
    { name: 'page[size]', value: size, condition: true }
  ])

  try {
    const response = await $apiHelper.articles.getArticles(params)

    if (!query || query === filter.value.query) {
      const { data, meta } = JsonApiHelper.denormalizeResponse(response as any) as any
      articlesData.value = data
      totalArticles.value = meta.totalArticles
      totalPages.value = helpers.getTotalPages(meta.totalArticles, size)

      isDataLoading.value = false
    }
  } catch (error) {
    console.error(error)

    $toast.error(messages.failedToGetArticles)
  }
}

function parseQuery () {
  const { isBlog } = route.query
  filter.value.isBlog = isBlog === 'yes' ? isBlog : null
}

parseQuery()
</script>

<template>
  <section>
    <div class="container">
      <Breadcrumbs :current="title" :items="breadcrumbsItems" />

      <Heading :badge-text="totalArticles" :title="title">
        <template #buttons>
          <BasicButton
            v-if="filter.isBlog"
            color="white"
            is-small
            title="Статьи"
            @click="goToArticlesPage"
          />
        </template>
      </Heading>

      <SearchInput v-show="showSearchInput" v-model="filter.query" class="mb-6" placeholder="Поиск статей" />

      <Preloader v-if="isDataLoading" />
      <div v-else-if="articlesData.length" class="-m-1 flex flex-wrap md:-m-2">
        <div v-for="x in articlesData" :key="x.id" class="w-1/2 p-1 md:w-1/3 md:p-2 xl:w-1/4">
          <CardWithImage :data="x" :path="helpers.getArticlePath(x)" />
        </div>
      </div>
      <div v-else class="content-wrapper">
        <Notification status="info">
          <template v-if="filter.query">
            Статьи не&nbsp;найдены.
          </template>
          <template v-else>
            Нет статей.
          </template>
        </Notification>
      </div>

      <Pagination
        class="mt-6"
        :current-page="page.number"
        :is-on-white-background="false"
        :last-page="totalPages"
        @change-page="changePage"
      />
    </div>
  </section>
</template>
